import type { PlaybackSegment } from '@lucimark/shared/helpers/playbackScheduling'
import type { ICachedAsset } from '@lucimark/shared/models/asset'
import type {
  IHlsVideoAsset,
  IQueueFrame,
  IUrlWidgetAsset,
  TCampaignAsset
} from '@/types'
import { serializePlaybackSegmentsForSequenceHash } from '@lucimark/shared/helpers/playbackScheduling'
import { sha256 } from 'js-sha256'
import { canonicalStringify } from '@/workers/registrationStatusWorkerHash'

/** Hash for empty frame sequence (SHA-256 of "[]") for change detection. */
export const HASH_EMPTY = sha256('[]')

/**
 * Serialize frames for hashing. Same format used by PlaybackScheduler so hashes match
 * when the queue is unchanged (id, duration, facets per frame).
 *
 * Includes playlist attribution + merged widget config + RSS directory ref: new scheduling can swap
 * `contentSourcePlaylistUrn`, tier-2/4 widget JSON, or `rssDirectoryRef.entryId` without changing frame id /
 * facets; omitting those caused `getQueueHash` to match, {@link PlaybackScheduleMaterializationService} skipped
 * `QUEUE_UPDATE`, and the UI kept resolving stale frames for several worker loops.
 */
export function serializeFramesForHash(frames: IQueueFrame[]): string {
  return frames
    .map((f) => {
      const {
        id,
        duration,
        facets,
        playContinuousWithNext,
        playlistUrn,
        contentSourcePlaylistUrn,
        playlistSeqNo,
        campaignWidgetConfig,
        playlistWidgetConfig,
        ctas,
        rssDirectoryRef
      } = f
      const extras = [
        playlistUrn,
        contentSourcePlaylistUrn ?? '',
        String(playlistSeqNo ?? 0),
        campaignWidgetConfig == null ? '' : canonicalStringify(campaignWidgetConfig),
        playlistWidgetConfig == null ? '' : canonicalStringify(playlistWidgetConfig),
        ctas == null || ctas.length === 0 ? '' : canonicalStringify(ctas),
        rssDirectoryRef == null ? '' : canonicalStringify(rssDirectoryRef)
      ].join('\t')
      return `${id} ${duration} ${playContinuousWithNext === true ? 1 : 0} ${extras} ${JSON.stringify(facets)}`
    })
    .join(' ')
}

/**
 * Compute SHA-256 hex hash of the queue for change detection.
 * Uses same serialization as PlaybackScheduler so frame sequencer hash can be compared with runtime queue hash.
 */
export function getQueueHash(frames: IQueueFrame[]): string {
  return frames.length === 0 ? HASH_EMPTY : sha256(serializeFramesForHash(frames))
}

/** Serialize only id and duration so sequence changes (not facets) drive loop reset. */
export function serializeQueueFramesSequenceForHash(frames: IQueueFrame[]): string {
  return frames.map(({ id, duration }) => `${id} ${duration ?? 0}`).join(' ')
}

/**
 * SHA-256 hex of the slim worker plan’s playable sequence (same string shape as {@link serializeQueueFramesSequenceForHash}).
 */
export function getPlaybackWorkerPayloadSequenceHash(segments: PlaybackSegment[]): string {
  return segments.length === 0
    ? HASH_EMPTY
    : sha256(serializePlaybackSegmentsForSequenceHash(segments))
}

/**
 * Hash of the playable sequence (id + duration only). Use this to decide whether to reset the loop:
 * when this differs we may replace the queue and reset; when only facets change this stays the same.
 */
export function getQueueSequenceHash(frames: IQueueFrame[]): string {
  return frames.length === 0 ? HASH_EMPTY : sha256(serializeQueueFramesSequenceForHash(frames))
}

/**
 * True when the two queues have the same playable sequence: same length, same id and duration per index.
 * Used to avoid resetting the loop when only facet/metadata changed (e.g. widget config); the current
 * frame then runs its full duration instead of jumping back to frame 0.
 */
export function isSameQueueSequence(a: IQueueFrame[], b: IQueueFrame[]): boolean {
  if (a.length !== b.length) { return false }
  return a.every((f, i) => {
    const g = b[i]
    return g != null && f.id === g.id && (f.duration ?? 0) === (g.duration ?? 0)
  })
}

/** Same playable sequence as {@link isSameQueueSequence} but for slim worker segments (id + duration only). */
export function isSamePlaybackPlanSequence(
  a: PlaybackSegment[],
  b: PlaybackSegment[]
): boolean {
  if (a.length !== b.length) {
    return false
  }
  return a.every((f, i) => {
    const g = b[i]
    return (
      g != null
      && f.frameId === g.frameId
      && (f.durationSeconds ?? 0) === (g.durationSeconds ?? 0)
    )
  })
}

/**
 * Same segment count and per-index {@link PlaybackSegment.durationSeconds} (ignores `frameId`).
 * Used when rotating embedded picks advance ids after a loop without changing loop length or timing.
 */
export function isSamePlaybackPlanTiming(
  a: PlaybackSegment[],
  b: PlaybackSegment[]
): boolean {
  if (a.length !== b.length) {
    return false
  }
  return a.every((f, i) => (f.durationSeconds ?? 0) === (b[i]?.durationSeconds ?? 0))
}

/**
 * Same spine slot per index: {@link PlaybackSegment.playlistUrn} and {@link PlaybackSegment.playlistSeqNo}.
 * Embedded rotating picks can change `frameId` while slots stay the same; distribution-list edits swap slots.
 */
export function isSamePlaybackPlanSpineSlots(
  a: PlaybackSegment[],
  b: PlaybackSegment[]
): boolean {
  if (a.length !== b.length) {
    return false
  }
  return a.every((f, i) => {
    const g = b[i]
    return (
      g != null
      && f.playlistUrn === g.playlistUrn
      && (f.playlistSeqNo ?? 0) === (g.playlistSeqNo ?? 0)
    )
  })
}

// --- Campaign asset list equivalence (player load / registration-status dedupe) ---

function cachedAssetEquivalent(a: ICachedAsset, b: ICachedAsset): boolean {
  if (a.blob != null && b.blob != null && a.blob === b.blob) {
    return true
  }
  if (
    typeof a.nativeFilePath === 'string'
    && a.nativeFilePath !== ''
    && a.nativeFilePath === b.nativeFilePath
    && a.etag !== ''
    && a.etag === b.etag
    && a.size === b.size
  ) {
    return true
  }
  return a.etag !== '' && a.etag === b.etag && a.size === b.size && a.type === b.type
}

function urlWidgetEquivalent(a: IUrlWidgetAsset, b: IUrlWidgetAsset): boolean {
  return canonicalStringify(a) === canonicalStringify(b)
}

function hlsEquivalent(a: IHlsVideoAsset, b: IHlsVideoAsset): boolean {
  return a.urn === b.urn && canonicalStringify(a.metadata) === canonicalStringify(b.metadata)
}

function campaignAssetPairEquivalent(a: TCampaignAsset, b: TCampaignAsset): boolean {
  if (a.urn !== b.urn) {
    return false
  }
  const aCached = a as ICachedAsset
  const bCached = b as ICachedAsset
  const aIsBinary = ('blob' in a && a.blob instanceof Blob) || (typeof aCached.nativeFilePath === 'string' && aCached.nativeFilePath !== '')
  const bIsBinary = ('blob' in b && b.blob instanceof Blob) || (typeof bCached.nativeFilePath === 'string' && bCached.nativeFilePath !== '')
  if (aIsBinary && bIsBinary) {
    return cachedAssetEquivalent(aCached, bCached)
  }
  if ('urlTemplate' in a && 'urlTemplate' in b) {
    return urlWidgetEquivalent(a as IUrlWidgetAsset, b as IUrlWidgetAsset)
  }
  if ('metadata' in a && 'metadata' in b && typeof (a as IHlsVideoAsset).metadata?.hlsUrl === 'string') {
    return hlsEquivalent(a as IHlsVideoAsset, b as IHlsVideoAsset)
  }
  return false
}

/** True when both lists carry the same URNs and each pair is equivalent for UI (blob ref/etag, URL widget fields, etc.). */
export function areCampaignAssetListsEquivalent(prev: TCampaignAsset[], next: TCampaignAsset[]): boolean {
  if (prev.length !== next.length) {
    return false
  }
  const nextByUrn = new Map(next.map(x => [x.urn, x]))
  for (const p of prev) {
    const n = nextByUrn.get(p.urn)
    if (n == null || !campaignAssetPairEquivalent(p, n)) {
      return false
    }
  }
  return true
}

/**
 * Fingerprint of the active distribution-list display row for worker debounce keys (membership identity only).
 */
export function getPlayerGroupPlaybackWorkerSyncFingerprint(
  pg: { id?: string | null } | null | undefined
): string {
  if (pg == null) {
    return '\0pg:null'
  }
  return canonicalStringify({
    id: pg.id ?? ''
  })
}
